import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAuthenticatedApiRequest from "../../AuthenticatedApiRequest";
import TchrURL from "../../TchrURL";
import { Subscription } from "../../types/billing";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { User } from "../user/useUser";

const useSubscription = () => {
  const queryClient = useQueryClient();
  const { post } = useAuthenticatedApiRequest();
  const { toast } = useToast();
  const { t } = useTranslation("common");

  const postTrialStart = async (): Promise<User> => {
    const url = new TchrURL("/user/starttrial");
    const response = await post(url, JSON.stringify({}));
    const data = response.json() as Promise<User>;
    if (response.ok) {
      if ((await data).trialUsed && (await data).creditsRemaining > 0) {
        return data;
      } else {
        throw new Error("Trial not started");
      }
    }
    throw new Error("Trial not started");
  };

  const postCancelSubscription = async (): Promise<Subscription> => {
    const url = new TchrURL("/payment/cancel");
    const response = await post(url, JSON.stringify({}));
    const data = response.json() as Promise<Subscription>;
    if (response.ok) {
      return data;
    }
    throw new Error("Failed to cancel subscription");
  };

  const usePauseSubscription = () =>
    useMutation({
      mutationKey: ["subscription", "pause"],
      mutationFn: async () => {
        const url = new TchrURL("/payment/pause");
        const response = await post(url, JSON.stringify({}));
        if (response.ok) {
          return { ok: true };
        }
      },
      onSuccess: () => {
        toast({
          title: t("toast.subscription.pause.title"),
          description: t("toast.subscription.pause.description"),
          variant: "default",
        });
        queryClient.invalidateQueries({ queryKey: ["user"] });
        queryClient.refetchQueries({ queryKey: ["user"] });
      },
    });

  const useUnpauseSubscription = () =>
    useMutation({
      mutationKey: ["subscription", "unpause"],
      mutationFn: async () => {
        const url = new TchrURL("/payment/unpause");
        const response = await post(url, JSON.stringify({}));
        if (response.ok) {
          return { ok: true };
        }
      },
      onSuccess: () => {
        toast({
          title: t("toast.subscription.unpause.title"),
          description: t("toast.subscription.unpause.description"),
          variant: "default",
        });
        queryClient.invalidateQueries({ queryKey: ["user"] });
        queryClient.refetchQueries({
          queryKey: ["user"],
          fetchStatus: "fetching",
        });
      },
    });

  const useStartTrial = () =>
    useMutation({
      mutationKey: ["user", "startFreeTrial"],
      mutationFn: postTrialStart,
      onSuccess: () => {
        toast({
          title: t("toast.subscription.trial.success.title"),
          description: t("toast.subscription.trial.success.description"),
          variant: "default",
        });
        queryClient.invalidateQueries({ queryKey: ["user"] });
        queryClient.refetchQueries({ queryKey: ["user"] });
      },
      onError: () => {
        toast({
          title: t("toast.subscription.trial.error.title"),
          description: t("toast.subscription.trial.error.description"),
          variant: "destructive",
        });
      },
    });

  const useCancelSubscription = () =>
    useMutation({
      mutationKey: ["subscription", "cancel"],
      mutationFn: postCancelSubscription,
      onSuccess: () => {
        toast({
          title: t("toast.subscription.cancel.title"),
          description: t("toast.subscription.cancel.description"),
          variant: "default",
        });
        queryClient.invalidateQueries({ queryKey: ["user"] });
        queryClient.refetchQueries({
          queryKey: ["user"],
        });
      },
      onError: () => {
        toast({
          title: t("toast.subscription.cancel.error.title"),
          description: t("toast.subscription.cancel.error.description"),
          variant: "destructive",
        });
      },
    });

  return {
    usePauseSubscription,
    useUnpauseSubscription,
    useStartTrial,
    useCancelSubscription,
  };
};

export default useSubscription;
